import { css } from 'aphrodite';
import * as React from 'react';
import {
	DragDropContext,
	Draggable,
	DraggableProvided,
	DropResult,
	Droppable,
	DroppableProvided,
} from 'react-beautiful-dnd';
import { DisclosureIcon } from '../../../../web/components/svgs/icons/DisclosureIcon';
import { DragGripperIcon } from '../../../../web/components/svgs/icons/DragGripperIcon';
import { styleSheet } from './styles';

export function AccordionDnDRoot({
	children,
	onDragEnd,
	topLevelDroppableType,
	topLevelDroppableId,
}: {
	topLevelDroppableType: string;
	topLevelDroppableId: string;
	children: React.ReactNode;
	onDragEnd: (result: DropResult) => void;
}) {
	return (
		<DragDropContext onDragEnd={onDragEnd}>
			<Droppable direction='vertical' droppableId={topLevelDroppableId} type={topLevelDroppableType}>
				{(droppableProvided: DroppableProvided) => {
					return (
						<div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
							{children}
						</div>
					);
				}}
			</Droppable>
		</DragDropContext>
	);
}

const AccordionTriggerContext = React.createContext<boolean>(null);

export function AccordionDraggableTrigger({
	draggableId,
	index,
	header,
	children,
	onMouseEnterTrigger,
}: {
	draggableId: string;
	index: number;
	header: React.ReactNode;
	children: React.ReactNode;
	onMouseEnterTrigger?: (ev: React.MouseEvent<HTMLDivElement>) => void;
}) {
	const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
	const toggleExpandable = () => setIsExpanded(state => !state);
	return (
		<AccordionTriggerContext.Provider value={isExpanded}>
			<Draggable disableInteractiveElementBlocking={true} draggableId={draggableId} index={index}>
				{(draggableProvided: DraggableProvided) => {
					return (
						<div
							className={css(styleSheet.topLevelDraggable)}
							ref={draggableProvided.innerRef}
							{...(draggableProvided.draggableProps || {})}
						>
							<div className={css(styleSheet.topLevelDraggableTitleRow)}>
								<span {...(draggableProvided?.dragHandleProps || {})} style={{ alignItems: 'center', display: 'flex' }}>
									<DragGripperIcon />
								</span>
								<div
									className={css(styleSheet.topLevelDraggableTrigger)}
									onMouseEnter={onMouseEnterTrigger}
									onClick={toggleExpandable}
								>
									<div className={css(styleSheet.topLevelDraggableTriggerContent)}>{header}</div>

									<span className={css(styleSheet.topLevelDraggableTriggerChevron, isExpanded && styleSheet.invert)}>
										<DisclosureIcon type='chevron' />
									</span>
								</div>
							</div>
							<div
								className={css(
									styleSheet.topLevelExpandableBody,
									isExpanded && styleSheet.topLevelExpandableBodyExpanded
								)}
							>
								{children}
							</div>
						</div>
					);
				}}
			</Draggable>
		</AccordionTriggerContext.Provider>
	);
}

export function AccordionDraggableContent({ children }: { children: React.ReactNode }) {
	const isExpanded = useAccordionTriggerExpanded();
	return isExpanded ? <>{children}</> : null;
}

export function useAccordionTriggerExpanded() {
	const value = React.useContext(AccordionTriggerContext);
	if (value == null) {
		throw new Error('useAccordionTriggerExpanded must be used within an AccordionDraggableTrigger');
	}
	return value;
}
